import { deleteJackpotConfiguration, getJackpotConfiguration, addJackpotConfiguration, JackpotConfigUpdate, JackpotConfigRequest } from 'apis/adminServices/jackpotServices';
import React, { useState, useEffect, memo } from 'react';
import SearchBar from '../searchbar';
import JackpotTable from './jackpotTable';
import Pagination from '../pagination';
import AddJackpotModal from './addJackpotModal';
import { useToastStore } from 'store/toastStore';

// Interface for AddJackpotModal
interface JackpotConfiguration {
  jackpotName: string;
  flights: number[];
  creditsForAnsweringRequest: number;
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

// Raw API Response Interfaces
interface JackpotConfigDetails {
  createdDate: string;
  answer: number;
  validFrom: string;
  validTo: string;
  status: 'Active' | 'Disabled';
}

interface JackpotConfigurationItem {
  jackpotId: number;
  airlineId: number;
}

interface RawJackpotConfigAPI {
  configName: string;
  details: JackpotConfigDetails;
  configurations: JackpotConfigurationItem[];
}

// Transformed interface for table display
export interface JackpotConfigAPI {
  configName: string;
  createdDate: string;
  status: 'Active' | 'Disabled';
  answer: number;
  validFrom: string;
  validTo: string;
  configurations: JackpotConfigurationItem[];
}

const EMPTY_JACKPOT: JackpotConfiguration = {
  jackpotName: '',
  flights: [],
  creditsForAnsweringRequest: 0,
  validFrom: '',
  validTo: '',
  isActive: true,
};

const JackpotSetting: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jackpots, setJackpots] = useState<JackpotConfigAPI[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showToast } = useToastStore();

  const [newJackpot, setNewJackpot] = useState<JackpotConfiguration>({ ...EMPTY_JACKPOT });

  const [pageSize, setPageSize] = useState(10);

  const transformApiData = (apiData: RawJackpotConfigAPI[]): JackpotConfigAPI[] => {
    return apiData.map(item => ({
      configName: item.configName,
      createdDate: new Date(item.details.createdDate).toLocaleDateString('en-GB'),
      status: item.details.status,
      answer: item.details.answer,
      validFrom: item.details.validFrom,
      validTo: item.details.validTo,
      configurations: item.configurations,
    }));
  };

  const fetchJackpotConfigurations = async () => {
    try {
      setLoading(true);
      const response = await getJackpotConfiguration({
        SearchWithName: searchTerm,
        PageNumber: currentPage,
        PageSize: pageSize
      });
      const transformedData = transformApiData(response.data || []);
      setJackpots(transformedData);
      setTotalRecords(response.totalRecords);
      setError(null);
    } catch (err: any) {
      // A 404 here means the search term matched nothing, not that the
      // request failed — show the table's empty state, not an error banner.
      if (err?.response?.status === 404) {
        setJackpots([]);
        setTotalRecords(0);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch jackpot configurations');
        console.error('Fetch Error:', err);
        setJackpots([]);
        setTotalRecords(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJackpotConfigurations();
  }, [searchTerm, currentPage, pageSize]);

  const handleDelete = async (id: number[], configName: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteJackpotConfiguration(id);
      await fetchJackpotConfigurations();
      showToast(`Jackpot configuration '${configName}' deleted successfully`, 'success');
    } catch (error) {
      console.error('Failed to delete jackpot configuration:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete jackpot configuration';
      setError(message);
      showToast(message, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleAddJackpot = async (config: JackpotConfigRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await addJackpotConfiguration(config);
      await fetchJackpotConfigurations();
      setNewJackpot({ ...EMPTY_JACKPOT });
      setIsModalOpen(false);
      showToast(response.message, 'success');
    } catch (error) {
      console.error('Error adding jackpot configuration:', error);
      const message = error instanceof Error ? error.message : 'Failed to add jackpot configuration';
      setError(message);
      throw error instanceof Error ? error : new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateJackpot = (updatedJackpot: JackpotConfigAPI) => {
    setJackpots(jackpots.map(j =>
      j.configName === updatedJackpot.configName ? updatedJackpot : j
    ));
  };
  
  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
        <p className="text-grease-100 text-sm font-semibold">Jackpot Settings</p>
        <div className="flex flex-wrap items-center gap-3">
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <button
            type="button"
            onClick={() => {
              setNewJackpot({ ...EMPTY_JACKPOT });
              setIsModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg hover:bg-[#E6C200] transition-colors"
            disabled={loading}
          >
            <span className="st-add text-white" />
            <p className="font-medium text-white text-sm">Add New</p>
          </button>
        </div>
      </div>

      {error && (
        <div className="text-center py-4 text-red-600">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-blue-600">Dismiss</button>
        </div>
      )}
      <JackpotTable
        jackpots={jackpots}
        onDelete={handleDelete}
        loading={loading}
        onUpdate={handleUpdateJackpot}
        refreshTable={fetchJackpotConfigurations}
      />
      <Pagination
        currentPage={currentPage}
        totalItems={totalRecords}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
      {isModalOpen && (
        <AddJackpotModal
          newJackpot={{
            jackpotName: newJackpot.jackpotName,
            flights: newJackpot.flights,
            creditsForAnsweringRequest: newJackpot.creditsForAnsweringRequest,
            validFrom: newJackpot.validFrom,
            validTo: newJackpot.validTo,
            isActive: newJackpot.isActive,
          }}
          setNewJackpot={setNewJackpot}
          onAdd={handleAddJackpot}
          onClose={() => {
            setNewJackpot({ ...EMPTY_JACKPOT });
            setIsModalOpen(false);
          }}
          isSubmitting={loading}
        />
      )}
    </section>
  );
};

export default memo(JackpotSetting);